import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Card, Loading, Empty } from "@/components/Shared";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/components/ui/sonner";
import { bulkArchive, bulkRestore, listArchived, massDeletePermission } from "@/lib/bulk";
import { getViews, createView, deleteView } from "@/lib/listViews";
import { ArrowUp, ArrowDown, ArrowUpDown, ChevronDown, Search, Pin, PinOff, Plus, ListFilter, Trash2, Archive, RotateCcw, X, Loader2, Filter } from "lucide-react";

const DELETABLE = new Set(["quotes", "orders", "invoices", "enquiries", "customers", "purchase-orders"]);
const rowKey = (r) => r.number || r.code || r.id;
const storeKey = (obj) => `tradedesk.views.${obj}`;
const loadCustom = (obj) => { try { return JSON.parse(localStorage.getItem(storeKey(obj)) || "[]"); } catch { return []; } };
const saveCustom = (obj, views) => localStorage.setItem(storeKey(obj), JSON.stringify(views));

// ----- field-based filter builder (client-side eval) -----
const OPS = {
  text: [["contains", "contains"], ["equals", "equals"], ["not_equals", "does not equal"], ["starts_with", "starts with"], ["is_empty", "is empty"], ["is_not_empty", "is not empty"]],
  select: [["equals", "equals"], ["not_equals", "does not equal"], ["is_empty", "is empty"], ["is_not_empty", "is not empty"]],
  number: [["equals", "="], ["not_equals", "≠"], ["gt", ">"], ["lt", "<"], ["gte", "≥"], ["lte", "≤"]],
  date: [["on", "on"], ["before", "before"], ["after", "after"], ["last_n_days", "in the last N days"]],
};
const opsFor = (field) => OPS[field?.type] || OPS.text;
const getPath = (row, key) => key.split(".").reduce((a, p) => a?.[p], row);
function matchOne(f, row) {
  const v = getPath(row, f.field), val = f.value, s = (x) => String(x ?? "").toLowerCase();
  switch (f.op) {
    case "equals": return s(v) === s(val);
    case "not_equals": return s(v) !== s(val);
    case "contains": return s(v).includes(s(val));
    case "starts_with": return s(v).startsWith(s(val));
    case "is_empty": return v == null || v === "";
    case "is_not_empty": return !(v == null || v === "");
    case "gt": return Number(v) > Number(val);
    case "lt": return Number(v) < Number(val);
    case "gte": return Number(v) >= Number(val);
    case "lte": return Number(v) <= Number(val);
    case "on": return !!v && String(v).slice(0, 10) === String(val).slice(0, 10);
    case "before": return !!v && new Date(v) < new Date(val);
    case "after": return !!v && new Date(v) > new Date(val);
    case "last_n_days": return !!v && new Date(v) >= new Date(Date.now() - Number(val) * 86400000);
    default: return true;
  }
}
const matchFilters = (filters, row) => (filters || []).every((f) => matchOne(f, row));

function FilterBuilderDialog({ open, fields, object, onClose, onSaved }) {
  const [name, setName] = useState("");
  const [filters, setFilters] = useState([]);
  const [busy, setBusy] = useState(false);
  const fieldOf = (key) => fields.find((f) => f.key === key) || fields[0];
  useEffect(() => { if (open) { setName(""); setFilters([{ field: fields[0].key, op: opsFor(fields[0])[0][0], value: "" }]); } }, [open, fields]);
  const setRow = (i, patch) => setFilters((fs) => fs.map((f, j) => (j === i ? { ...f, ...patch } : f)));
  const addRow = () => setFilters((fs) => [...fs, { field: fields[0].key, op: opsFor(fields[0])[0][0], value: "" }]);
  const rmRow = (i) => setFilters((fs) => fs.filter((_, j) => j !== i));
  const needsValue = (op) => !["is_empty", "is_not_empty"].includes(op);
  const save = async () => {
    setBusy(true);
    try { const { data } = await createView({ object, name: name.trim(), filters }); onSaved(data); }
    catch { toast.error("Could not save view"); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="filter-builder">
        <DialogHeader><DialogTitle>New list view</DialogTitle></DialogHeader>
        <Input data-testid="fb-name" placeholder="View name (e.g. Urgent · New)" value={name} onChange={(e) => setName(e.target.value)} className="h-10" />
        <div className="text-xs font-medium text-[#6B7189]">Matching ALL of these filters</div>
        <div className="space-y-2 max-h-[45vh] overflow-y-auto pr-1">
          {filters.map((f, i) => {
            const fld = fieldOf(f.field), ops = opsFor(fld);
            return (
              <div key={i} className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2 items-center" data-testid={`fb-row-${i}`}>
                <select value={f.field} onChange={(e) => { const nf = fieldOf(e.target.value); setRow(i, { field: e.target.value, op: opsFor(nf)[0][0], value: "" }); }} className="h-9 rounded-md border border-[#E6E8F0] px-2 text-sm bg-white" data-testid={`fb-field-${i}`}>
                  {fields.map((x) => <option key={x.key} value={x.key}>{x.label}</option>)}
                </select>
                <select value={f.op} onChange={(e) => setRow(i, { op: e.target.value })} className="h-9 rounded-md border border-[#E6E8F0] px-2 text-sm bg-white" data-testid={`fb-op-${i}`}>
                  {ops.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
                {needsValue(f.op) ? (
                  fld.type === "select" ? (
                    <select value={f.value} onChange={(e) => setRow(i, { value: e.target.value })} className="h-9 rounded-md border border-[#E6E8F0] px-2 text-sm bg-white" data-testid={`fb-value-${i}`}>
                      <option value="">—</option>{(fld.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  ) : (
                    <Input value={f.value} onChange={(e) => setRow(i, { value: e.target.value })} className="h-9" data-testid={`fb-value-${i}`}
                      type={fld.type === "number" || f.op === "last_n_days" ? "number" : fld.type === "date" ? "date" : "text"} placeholder="Value" />
                  )
                ) : <div className="text-xs text-[#9AA0B5] px-1">no value</div>}
                <button onClick={() => rmRow(i)} className="text-[#F83A84] p-1" data-testid={`fb-remove-${i}`}><Trash2 className="w-4 h-4" /></button>
              </div>);
          })}
        </div>
        <button onClick={addRow} className="text-sm text-[#1D4ED8] flex items-center gap-1 w-fit" data-testid="fb-add"><Plus className="w-4 h-4" /> Add filter</button>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-[#101D51]" disabled={!name.trim() || busy} onClick={save} data-testid="fb-save">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save view"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function matchCustom(view, row, me) {
  if (view.statuses?.length && !view.statuses.includes(row.display_status || row.status)) return false;
  if (view.mine && !(row.assigned_to === me || row.created_by === me)) return false;
  return true;
}

function SaveViewDialog({ open, onClose, statuses, onSave }) {
  const [name, setName] = useState(""); const [sel, setSel] = useState([]); const [mine, setMine] = useState(false);
  useEffect(() => { if (open) { setName(""); setSel([]); setMine(false); } }, [open]);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>New list view</DialogTitle></DialogHeader>
        <Input data-testid="view-name" placeholder="View name (e.g. My open quotes)" value={name} onChange={(e) => setName(e.target.value)} className="h-10" />
        <div className="text-xs text-[#6B7189]">Status filter</div>
        <div className="flex flex-wrap gap-1">
          {statuses.map((s) => <button key={s} onClick={() => setSel(sel.includes(s) ? sel.filter((x) => x !== s) : [...sel, s])} data-testid={`view-status-${s.replace(/\s+/g, "-").toLowerCase()}`}
            className={`px-3 h-8 rounded-full text-xs border ${sel.includes(s) ? "bg-[#101D51] text-white border-[#101D51]" : "bg-white text-[#6B7189] border-[#E6E8F0]"}`}>{s}</button>)}
        </div>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={mine} onChange={(e) => setMine(e.target.checked)} data-testid="view-mine" /> Only records assigned to / created by me</label>
        <DialogFooter><Button className="bg-[#101D51]" disabled={!name.trim()} onClick={() => onSave({ id: `c-${Date.now()}`, name: name.trim(), statuses: sel, mine, custom: true })} data-testid="view-save">Save view</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Mobile card: 3 key fields visible, rest behind "More".
function RowCard({ r, columns, onOpen, selectable, selected, onToggle }) {
  const [more, setMore] = useState(false);
  const cell = (c) => (c.render ? c.render(r) : r[c.key] ?? "—");
  const [first, second, third, ...rest] = columns;
  return (
    <Card className="p-3" data-testid={`list-card-${rowKey(r)}`}>
      <div className="flex items-start gap-2">
        {selectable && (
          <input type="checkbox" checked={selected} onChange={() => onToggle(r.id)} className="mt-1 w-4 h-4 accent-[#101D51]" data-testid={`bulk-select-m-${r.id}`} onClick={(e) => e.stopPropagation()} />
        )}
        <button onClick={onOpen} className="w-full text-left flex items-start justify-between gap-3 min-h-[44px]" data-testid={`list-card-open-${rowKey(r)}`}>
          <div className="min-w-0 flex-1">
            <div className="text-[#1D4ED8] font-semibold text-sm truncate">{cell(first)}</div>
            {second && <div className="text-sm text-[#101D51] truncate mt-0.5">{cell(second)}</div>}
          </div>
          {third && <div className="shrink-0 text-right text-sm">{cell(third)}</div>}
        </button>
      </div>
      {rest.length > 0 && (
        <>
          <button onClick={() => setMore(!more)} className="mt-1 text-xs text-[#6B7189] flex items-center gap-1 min-h-[36px]" data-testid={`list-card-more-${rowKey(r)}`}>
            <ChevronDown className={`w-3.5 h-3.5 transition-transform ${more ? "rotate-180" : ""}`} /> {more ? "Less" : `${rest.length} more`}
          </button>
          {more && <dl className="grid grid-cols-2 gap-x-3 gap-y-2 text-sm border-t border-[#F0F1F5] pt-2">
            {rest.map((c) => <div key={c.key} className="min-w-0"><dt className="text-[10px] uppercase tracking-wide text-[#6B7189]">{c.label}</dt><dd className="text-[#101D51] truncate">{cell(c)}</dd></div>)}
          </dl>}
        </>)}
    </Card>);
}

function ConfirmArchiveDialog({ open, count, label, busy, onClose, onConfirm }) {
  const [text, setText] = useState("");
  useEffect(() => { if (open) setText(""); }, [open]);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="bulk-confirm-dialog">
        <DialogHeader><DialogTitle>Delete {count} {label}{count === 1 ? "" : "s"}?</DialogTitle></DialogHeader>
        <p className="text-sm text-[#6B7189]">These records will be archived and removed from your lists. You can restore them later from <span className="font-medium text-[#101D51]">Archived</span>. Records with dependents (e.g. paid invoices, customers with orders) are skipped automatically.</p>
        <div className="text-xs text-[#6B7189]">Type <span className="font-mono font-semibold text-[#F83A84]">DELETE</span> to confirm</div>
        <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="DELETE" className="h-10" data-testid="bulk-confirm-input" autoFocus />
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="bulk-confirm-cancel">Cancel</Button>
          <Button className="bg-[#F83A84] hover:bg-[#d62f70]" disabled={text !== "DELETE" || busy} onClick={onConfirm} data-testid="bulk-confirm-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Trash2 className="w-4 h-4" /> Delete {count}</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ArchivedDialog({ open, object, label, onClose, onChanged }) {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);
  const reload = () => { setRows(null); listArchived(object).then((r) => setRows(r.data.items)).catch(() => setRows([])); };
  useEffect(() => { if (open) reload(); /* eslint-disable-next-line */ }, [open, object]);
  const restore = async (ids) => {
    setBusy(true);
    try { const { data } = await bulkRestore(object, ids); toast.success(`${data.count} restored`); onChanged?.(); reload(); }
    catch { toast.error("Restore failed"); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="archived-dialog">
        <DialogHeader><DialogTitle>Archived {label}s</DialogTitle></DialogHeader>
        {!rows ? <Loading /> : rows.length === 0 ? <Empty icon={Archive} title="Nothing archived" hint="Deleted records show up here for recovery." /> : (
          <>
            <div className="flex justify-end">
              <Button variant="outline" className="h-8" disabled={busy} onClick={() => restore(rows.map((r) => r.id))} data-testid="restore-all-btn"><RotateCcw className="w-3.5 h-3.5" /> Restore all</Button>
            </div>
            <div className="max-h-[50vh] overflow-y-auto divide-y divide-[#F0F1F5] border border-[#E6E8F0] rounded-lg">
              {rows.map((r) => (
                <div key={r.id} className="flex items-center gap-2 p-2.5" data-testid={`archived-row-${r.id}`}>
                  <div className="min-w-0 flex-1"><div className="text-sm font-medium text-[#101D51] truncate">{r.title} {r.status && <span className="text-[11px] text-[#6B7189] font-normal">· {r.status}</span>}</div><div className="text-[11px] text-[#6B7189] truncate">{r.sub}</div></div>
                  <Button variant="ghost" className="h-8 text-[#0B7C5A]" disabled={busy} onClick={() => restore([r.id])} data-testid={`restore-${r.id}`}><RotateCcw className="w-3.5 h-3.5" /> Restore</Button>
                </div>))}
            </div>
          </>)}
      </DialogContent>
    </Dialog>
  );
}

export default function ListView({ object, title, rows, columns, views, statuses, defaultSort, me, onRow, actions, searchKeys = ["number"], loading, headerExtra, onDataChange, fields = null, owner = false }) {
  const navigate = useNavigate();
  const [custom, setCustom] = useState(() => loadCustom(object));
  const [serverViews, setServerViews] = useState([]);
  const loadServer = useCallback(() => { if (!fields) return; getViews(object).then((r) => setServerViews(r.data.items)).catch(() => {}); }, [fields, object]);
  useEffect(() => { loadServer(); }, [loadServer]);
  const allViews = useMemo(() => (fields ? [...views, ...serverViews] : [...views, ...custom]), [views, custom, serverViews, fields]);
  const [viewId, setViewId] = useState(() => localStorage.getItem(`${storeKey(object)}.pinned`) || views[0].id);
  const view = allViews.find((v) => v.id === viewId) || views[0];
  const [q, setQ] = useState("");
  const [sort, setSort] = useState(defaultSort || { key: columns[0].key, dir: "desc" });
  const [dlg, setDlg] = useState(false);
  const [menu, setMenu] = useState(false);

  // ----- bulk delete / archive -----
  const [canDel, setCanDel] = useState(false);
  const [sel, setSel] = useState(() => new Set());
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [archOpen, setArchOpen] = useState(false);
  const label = { quotes: "quote", orders: "order", invoices: "invoice", enquiries: "enquiry", customers: "customer", "purchase-orders": "purchase order" }[object] || "record";
  useEffect(() => { if (DELETABLE.has(object)) massDeletePermission().then((r) => setCanDel(!!r.data?.can_mass_delete)).catch(() => {}); }, [object]);
  useEffect(() => { setSel(new Set()); }, [viewId, object]);

  const filtered = useMemo(() => {
    let r = rows || [];
    r = view.filters ? r.filter((x) => matchFilters(view.filters, x)) : view.custom ? r.filter((x) => matchCustom(view, x, me)) : r.filter((x) => view.filter(x, me));
    if (q.trim()) { const ql = q.toLowerCase(); r = r.filter((x) => searchKeys.some((k) => String(k.split(".").reduce((a, p) => a?.[p], x) ?? "").toLowerCase().includes(ql))); }
    const col = columns.find((c) => c.key === sort.key);
    const get = (x) => (col?.sortValue ? col.sortValue(x) : sort.key.split(".").reduce((a, p) => a?.[p], x));
    return [...r].sort((a, b) => { const va = get(a) ?? "", vb = get(b) ?? ""; const c = typeof va === "number" && typeof vb === "number" ? va - vb : String(va).localeCompare(String(vb)); return sort.dir === "asc" ? c : -c; });
  }, [rows, view, q, sort, columns, me, searchKeys]);

  const pinned = localStorage.getItem(`${storeKey(object)}.pinned`) === view.id;
  const pin = () => { if (pinned) localStorage.removeItem(`${storeKey(object)}.pinned`); else localStorage.setItem(`${storeKey(object)}.pinned`, view.id); setMenu(false); toast.success(pinned ? "Unpinned" : "Pinned as default"); };
  const del = async (v) => {
    if (v.filters) {
      try { await deleteView(v.id); } catch { toast.error("Only the creator or Owner can delete this view"); return; }
      if (viewId === v.id) setViewId(views[0].id); loadServer(); toast.success("View deleted"); return;
    }
    const next = custom.filter((x) => x.id !== v.id); setCustom(next); saveCustom(object, next); if (viewId === v.id) setViewId(views[0].id);
  };
  const toggleSort = (key) => setSort((s) => ({ key, dir: s.key === key && s.dir === "desc" ? "asc" : "desc" }));
  const summary = columns.filter((c) => c.sum).map((c) => ({ label: c.label, value: c.render({ [c.key]: filtered.reduce((s, r) => s + (r[c.key] || 0), 0) }) }));

  const toggle = (id) => setSel((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const allSelected = filtered.length > 0 && filtered.every((r) => sel.has(r.id));
  const toggleAll = () => setSel(() => (allSelected ? new Set() : new Set(filtered.map((r) => r.id))));
  const doArchive = async () => {
    setBusy(true);
    try {
      const { data } = await bulkArchive(object, [...sel]);
      if (data.count) toast.success(`${data.count} ${label}${data.count === 1 ? "" : "s"} deleted`);
      if (data.skipped?.length) toast.warning(`${data.skipped.length} skipped — ${data.skipped[0].reason}`);
      if (!data.count && !data.skipped?.length) toast.info("Nothing deleted");
      setSel(new Set()); setConfirm(false); onDataChange?.();
    } catch { toast.error("Delete failed"); } finally { setBusy(false); }
  };

  return (
    <div data-testid={`listview-${object}`}>
      <Card className="p-3 sm:p-4 mb-3">
        <div className="flex flex-col lg:flex-row lg:items-center gap-3">
          <div className="relative">
            <button onClick={() => setMenu(!menu)} className="flex items-center gap-2 text-left" data-testid="view-picker">
              <div><div className="text-[11px] text-[#6B7189]">{title}</div><div className="text-lg font-bold text-[#101D51] flex items-center gap-1">{view.name} <ChevronDown className="w-4 h-4 text-[#6B7189]" /></div></div>
            </button>
            {menu && (
              <div className="absolute z-40 mt-1 w-72 bg-white rounded-xl shadow-xl border border-[#E6E8F0] p-1" data-testid="view-menu">
                <div className="px-3 py-1 text-[10px] uppercase tracking-wide text-[#6B7189]">List views</div>
                {allViews.map((v) => (
                  <div key={v.id} className={`flex items-center rounded-lg ${v.id === view.id ? "bg-[#F5F6FA]" : "hover:bg-[#F5F6FA]"}`}>
                    <button onClick={() => { setViewId(v.id); setMenu(false); }} className="flex-1 text-left px-3 py-2 text-sm text-[#101D51]" data-testid={`view-${v.id}`}>{v.name}</button>
                    {(v.custom || (v.filters && (owner || v.created_by === me))) && <button onClick={() => del(v)} className="px-2 text-xs text-[#F83A84]" data-testid={`view-del-${v.id}`}>Delete</button>}
                  </div>))}
                <div className="border-t border-[#E6E8F0] mt-1 pt-1 flex">
                  <button onClick={() => { setDlg(true); setMenu(false); }} className="flex-1 text-left px-3 py-2 text-sm text-[#1D4ED8] flex items-center gap-2" data-testid="view-new">{fields ? <Filter className="w-4 h-4" /> : <Plus className="w-4 h-4" />} New view</button>
                  <button onClick={pin} className="px-3 py-2 text-sm text-[#6B7189] flex items-center gap-1" data-testid="view-pin">{pinned ? <PinOff className="w-4 h-4" /> : <Pin className="w-4 h-4" />}</button>
                </div>
              </div>)}
          </div>
          <div className="text-xs text-[#6B7189] lg:ml-2" data-testid="view-count">{filtered.length} item{filtered.length === 1 ? "" : "s"} · sorted by {columns.find((c) => c.key === sort.key)?.label} {sort.dir === "asc" ? "↑" : "↓"}
            {summary.map((s) => <span key={s.label} className="ml-2 font-medium text-[#101D51]">{s.label} {s.value}</span>)}</div>
          <div className="flex-1" />
          {headerExtra}
          {canDel && <Button variant="outline" className="h-9" onClick={() => setArchOpen(true)} data-testid="view-archived-btn"><Archive className="w-4 h-4" /> Archived</Button>}
          <div className="relative w-full lg:w-64"><Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7189]" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search this list…" className="pl-9 h-9" data-testid="list-search" /></div>
          <div className="flex gap-2">{actions}</div>
        </div>
      </Card>
      {loading ? <Loading /> : filtered.length === 0 ? <Empty icon={ListFilter} title="No records in this view" hint="Try another list view or clear the search." /> : (<>
        <div className="md:hidden space-y-2" data-testid="list-cards">
          {filtered.map((r) => <RowCard key={r.id} r={r} columns={columns} selectable={canDel} selected={sel.has(r.id)} onToggle={toggle} onOpen={() => (onRow ? onRow(r) : navigate(r.path))} />)}
        </div>
        <Card className="overflow-x-auto hidden md:block">
          <table className="w-full text-sm" data-testid="list-table">
            <thead className="bg-[#F5F6FA] text-[11px] uppercase tracking-wide text-[#6B7189] select-none">
              <tr>
                {canDel && <th className="px-3 py-2 w-8"><input type="checkbox" checked={allSelected} onChange={toggleAll} className="w-4 h-4 accent-[#101D51]" data-testid="bulk-select-all" /></th>}
                <th className="px-3 py-2 text-left w-8">#</th>
                {columns.map((c) => (
                  <th key={c.key} onClick={() => toggleSort(c.key)} className={`px-3 py-2 font-medium whitespace-nowrap cursor-pointer hover:text-[#101D51] ${c.right ? "text-right" : "text-left"}`} data-testid={`sort-${c.key}`}>
                    <span className="inline-flex items-center gap-1">{c.label}{sort.key === c.key ? (sort.dir === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />) : <ArrowUpDown className="w-3 h-3 opacity-40" />}</span>
                  </th>))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((r, i) => (
                <tr key={r.id} onClick={() => (onRow ? onRow(r) : navigate(r.path))} data-testid={`list-row-${rowKey(r)}`} className={`border-t border-[#F0F1F5] cursor-pointer hover:bg-[#F5F6FA] ${sel.has(r.id) ? "bg-[#EEF2FF]" : ""}`}>
                  {canDel && <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}><input type="checkbox" checked={sel.has(r.id)} onChange={() => toggle(r.id)} className="w-4 h-4 accent-[#101D51]" data-testid={`bulk-select-${r.id}`} /></td>}
                  <td className="px-3 py-2 text-[#6B7189] text-xs">{i + 1}</td>
                  {columns.map((c, j) => <td key={c.key} className={`px-3 py-2 whitespace-nowrap ${c.right ? "text-right" : ""} ${j === 0 ? "text-[#1D4ED8] font-medium" : "text-[#101D51]"}`}>{c.render ? c.render(r) : r[c.key] ?? "—"}</td>)}
                </tr>))}
            </tbody>
          </table>
        </Card></>)}

      {canDel && sel.size > 0 && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 bg-[#101D51] text-white rounded-full shadow-2xl pl-5 pr-2 py-2" data-testid="bulk-bar">
          <span className="text-sm font-medium" data-testid="bulk-count">{sel.size} selected</span>
          <button onClick={() => setSel(new Set())} className="text-xs text-white/70 hover:text-white flex items-center gap-1" data-testid="bulk-clear-btn"><X className="w-3.5 h-3.5" /> Clear</button>
          <Button className="h-9 rounded-full bg-[#F83A84] hover:bg-[#d62f70]" onClick={() => setConfirm(true)} data-testid="bulk-archive-btn"><Trash2 className="w-4 h-4" /> Delete</Button>
        </div>)}

      <ConfirmArchiveDialog open={confirm} count={sel.size} label={label} busy={busy} onClose={() => setConfirm(false)} onConfirm={doArchive} />
      <ArchivedDialog open={archOpen} object={object} label={label} onClose={() => setArchOpen(false)} onChanged={onDataChange} />
      {fields
        ? <FilterBuilderDialog open={dlg} fields={fields} object={object} onClose={() => setDlg(false)} onSaved={(v) => { setDlg(false); loadServer(); setViewId(v.id); toast.success("View saved"); }} />
        : <SaveViewDialog open={dlg} onClose={() => setDlg(false)} statuses={statuses} onSave={(v) => { const next = [...custom, v]; setCustom(next); saveCustom(object, next); setViewId(v.id); setDlg(false); toast.success("View saved"); }} />}
    </div>
  );
}
